import React, {
  useEffect,
  useState,
} from "react";

function clean(value) {
  return String(value == null ? "" : value).trim();
}

export default function TextbookVersionHistory({
  open = false,
  projectId,
  isFR = false,
  onLoad,
  onPreview,
  onClose,
}) {
  const [loading, setLoading] =
    useState(false);
  const [error, setError] =
    useState("");
  const [snapshots, setSnapshots] =
    useState([]);
  const [preview, setPreview] =
    useState(null);
  const [previewLoading, setPreviewLoading] =
    useState(false);

  useEffect(() => {
    if (!open) return;

    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setPreview(null);

        if (typeof onLoad !== "function") {
          throw new Error(
            isFR
              ? "L’historique des versions n’est pas disponible."
              : "Version History is unavailable."
          );
        }

        const payload =
          await onLoad(projectId);

        if (!active) return;

        setSnapshots(
          Array.isArray(payload?.snapshots)
            ? payload.snapshots
            : []
        );
      } catch (loadError) {
        if (!active) return;
        setError(
          clean(loadError?.message) ||
            (isFR
              ? "Impossible d’ouvrir l’historique des versions."
              : "Could not open Version History.")
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [open, projectId, isFR, onLoad]);

  if (!open) return null;

  return (
    <div
      data-textbook-version-history-overlay="true"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose?.();
        }
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        display: "grid",
        justifyItems: "end",
        background: "rgba(15,23,42,0.34)",
        backdropFilter: "blur(2px)",
      }}
    >
      <section
        data-textbook-version-history="true"
        role="dialog"
        aria-modal="true"
        aria-label={
          isFR
            ? "Historique des versions"
            : "Version History"
        }
        style={{
          width: "min(560px, 94vw)",
          height: "100%",
          maxHeight: "100vh",
          overflowY: "auto",
          padding: 18,
          borderRadius: "18px 0 0 18px",
          border: "1px solid rgba(15,118,110,0.18)",
          background: "#f8fafc",
          boxShadow: "-18px 0 42px rgba(15,23,42,0.18)",
        }}
      >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontWeight: 850,
              color: "#0f5132",
            }}
          >
            {isFR
              ? "Historique des versions"
              : "Version History"}
          </div>
          <div
            className="ns-card-meta"
            style={{ marginTop: 4 }}
          >
            {isFR
              ? "Copies de sécurité enregistrées automatiquement avant les modifications importantes."
              : "Automatic safety copies saved before important changes."}
          </div>
        </div>

        <button
          type="button"
          className="ns-button ns-button-outline"
          onClick={onClose}
        >
          {isFR ? "Fermer" : "Close"}
        </button>
      </div>

      {loading ? (
        <div
          className="ns-card-meta"
          style={{ marginTop: 12 }}
        >
          {isFR
            ? "Chargement de l’historique…"
            : "Loading version history…"}
        </div>
      ) : null}

      {error ? (
        <div
          role="alert"
          style={{
            marginTop: 12,
            color: "#b91c1c",
            fontWeight: 700,
          }}
        >
          {error}
        </div>
      ) : null}

      {!loading && !error &&
      snapshots.length === 0 ? (
        <div
          className="ns-card-meta"
          style={{ marginTop: 12 }}
        >
          {isFR
            ? "Aucune version de sécurité n’est encore disponible."
            : "No safety versions are available yet."}
        </div>
      ) : null}

      {snapshots.length > 0 ? (
        <div
          style={{
            display: "grid",
            gap: 8,
            marginTop: 12,
          }}
        >
          {snapshots.map((entry) => (
            <div
              key={entry.id}
              style={{
                padding: 10,
                borderRadius: 12,
                background: "#fff",
                border:
                  "1px solid rgba(148,163,184,0.28)",
                display: "flex",
                justifyContent:
                  "space-between",
                gap: 10,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div>
                <div style={{ fontWeight: 800 }}>
                  {entry.createdAt
                    ? new Date(
                        entry.createdAt
                      ).toLocaleString()
                    : (isFR
                        ? "Version enregistrée"
                        : "Saved version")}
                </div>
                <div
                  className="ns-card-meta"
                  style={{ marginTop: 3 }}
                >
                  {clean(entry.reason) ||
                    (isFR
                      ? "Copie de sécurité"
                      : "Safety snapshot")}
                  {" · "}
                  {Number(
                    entry.chapterCount || 0
                  )}
                  {isFR
                    ? " chapitres"
                    : " chapters"}
                  {" · "}
                  {Number(
                    entry.placedFigureCount || 0
                  )}
                  {isFR
                    ? " figures placées"
                    : " placed figures"}
                  {" · "}
                  {Number(
                    entry.figureAssetCount || 0
                  )}
                  {isFR
                    ? " ressources stockées"
                    : " stored assets"}
                </div>
              </div>

              <button
                type="button"
                className="ns-button ns-button-outline"
                disabled={previewLoading}
                onClick={async () => {
                  try {
                    setPreviewLoading(true);
                    setError("");

                    if (
                      typeof onPreview !==
                      "function"
                    ) {
                      throw new Error(
                        isFR
                          ? "L’aperçu n’est pas disponible."
                          : "Restore Preview is unavailable."
                      );
                    }

                    const payload =
                      await onPreview(
                        projectId,
                        entry.id
                      );

                    setPreview(
                      payload?.snapshot || null
                    );
                  } catch (previewError) {
                    setError(
                      clean(
                        previewError?.message
                      ) ||
                        (isFR
                          ? "Impossible d’ouvrir l’aperçu de cette version."
                          : "Could not open this version preview.")
                    );
                  } finally {
                    setPreviewLoading(false);
                  }
                }}
              >
                {isFR
                  ? "Aperçu"
                  : "Restore Preview"}
              </button>
            </div>
          ))}
        </div>
      ) : null}

      {preview ? (
        <div
          data-textbook-version-preview="true"
          style={{
            marginTop: 14,
            padding: 12,
            borderRadius: 12,
            background: "#fff",
            border:
              "1px solid rgba(15,118,110,0.22)",
          }}
        >
          <div
            style={{
              fontWeight: 850,
              color: "#0f5132",
            }}
          >
            {isFR
              ? "Aperçu de restauration"
              : "Restore Preview"}
          </div>

          <div
            className="ns-card-meta"
            style={{
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {isFR
              ? "Lecture seule — rien ne sera modifié à cette étape."
              : "Read-only — nothing will be changed at this stage."}
          </div>

          <div
            style={{
              marginTop: 10,
              display: "grid",
              gap: 5,
            }}
          >
            <div>
              {isFR
                ? "Révision de la Structure du livre"
                : "Book Map revision"}
              : {Number(
                preview.projectPlanRevision || 0
              )}
            </div>
            <div>
              {isFR
                ? "Chapitres enregistrés"
                : "Saved chapters"}
              : {Array.isArray(preview.chapters)
                ? preview.chapters.length
                : 0}
            </div>
            <div>
              {isFR
                ? "Figures enregistrées"
                : "Saved figures"}
              : {Array.isArray(preview.figures)
                ? preview.figures.length
                : 0}
            </div>
            <div>
              {isFR
                ? "Éléments de la Structure du livre"
                : "Book Map items"}
              : {Array.isArray(
                  preview?.bookMap?.items
                )
                ? preview.bookMap.items.length
                : 0}
            </div>
          </div>
        </div>
      ) : null}
      </section>
    </div>
  );
}
